"use client"

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import {
  EmailAddressSuggestion,
  EmailMessageDraft,
  LexicalText,
  STARTING_NODES,
  SyncboxEmailDraftParams,
} from "@/schema"
import { useEmailComposerStore } from "@/stores/use-email-composer-store"
import { setCloseAttemptHandler, useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { usePinnedDialogStore } from "@/stores/use-pinned-dialog-store"
import { useVoiceTranscriptionStore } from "@/stores/use-voice-transcription-store"
import { ScheduledSendButton } from "@ui/scheduled-send/scheduled-send-button"
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { AlertTriangle, Loader2, Mic, Paperclip, PictureInPicture2, Save, Sparkles, X } from "lucide-react"
import { toast } from "sonner"

import { useReviseEmail } from "@/hooks/api/email_messages"
import { useFetchHousehold } from "@/hooks/api/households/queries"
import {
  useCreateSyncboxEmailDraft,
  useSyncboxEmailDraft,
  useUpdateSyncboxEmailDraft,
} from "@/hooks/api/syncbox_email_drafts"
import { useFetchUsersWithEmails } from "@/hooks/api/users/queries"
import { useBookId } from "@/hooks/use-book-id"
import { useEditorFocused } from "@/hooks/use-editor-focused"
import { useEmailComposeLogic } from "@/hooks/use-email-compose-logic"
import { ConfirmDialog } from "@/components/dialogs"
import { CustomDialogContent } from "@/components/dialogs/custom-dialog-content"
import { useDialogCloseRegistry } from "@/components/dialogs/dialog-close-context"
import { useDialogEntryId } from "@/components/dialogs/dialog-entry-context"
import EmailBodyEditor from "@/components/email/body-editor"
import { EmailReferenceInput } from "@/components/email/email-reference-input"
import { EmailTemplatePicker } from "@/components/email/email-template-picker"
import { FloatingFormatBar } from "@/components/email/floating-format-bar"
import { FromEmailSelector } from "@/components/email/from-email-selector"
import {
  EMAIL_EDITOR_SYNC_ERROR,
  hasVisibleOutgoingContent,
  readOutgoingContent,
} from "@/components/email/outgoing-content"
import { resolveCompositionSender } from "@/components/email/sender-addresses"
import { ensureHtmlContent, removeTrailingSignature } from "@/components/email/signature-utils"
import { EditorRefHandle } from "@/components/lexical/editors/email"
import { htmlToNodes, mergeLexicalContents } from "@/components/lexical/serializer"

const MAX_ATTACHMENTS = 3
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB per file
const MAX_TOTAL_ATTACHMENT_SIZE = 25 * 1024 * 1024 // 25MB total (Nylas multipart limit)

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i]
}

export function EmailSubjectInput({
  value,
  onChange,
  autoFocus = true,
  showLabel = false,
  disabled = false,
  className,
  inputClassName,
}: {
  value: string
  onChange: (value: string) => void
  autoFocus?: boolean
  showLabel?: boolean
  disabled?: boolean
  className?: string
  inputClassName?: string
}) {
  return (
    <div className={cn("flex flex-none items-center border-b", className)}>
      {showLabel && (
        <Label htmlFor="email-subject" className="text-muted-foreground pl-2 text-xs font-normal">
          Subject
        </Label>
      )}
      <Input
        id="email-subject"
        autoFocus={autoFocus}
        type="text"
        placeholder="Add a subject"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "placeholder:text-input-placeholder rounded-none border-0 p-0 px-4 font-medium shadow-none focus-visible:ring-0",
          showLabel && "pl-2",
          inputClassName
        )}
        disabled={disabled}
      />
    </div>
  )
}

export function ComposeEmail({
  syncboxDraftId,
  replyToSyncboxMessageId,
  draftId: initialDraftId,
  toEmails,
  ccEmails,
  bccEmails,
  recommendedEmails,
  householdId,
  onSent,
  onClose,
  startingContent,
  startingHTML,
  startingSubject,
  hidePinning = false,
  embedded = false,
  integrationSelectionOnly = false,
  onDraftChange,
}: {
  syncboxDraftId?: string
  replyToSyncboxMessageId?: string
  draftId?: string
  toEmails?: EmailAddressSuggestion[]
  ccEmails?: EmailAddressSuggestion[]
  bccEmails?: EmailAddressSuggestion[]
  recommendedEmails?: EmailAddressSuggestion[]
  householdId?: string
  onSent?: () => void
  onClose?: () => void
  startingContent?: LexicalText
  startingHTML?: string
  startingSubject?: string
  hidePinning?: boolean
  embedded?: boolean
  integrationSelectionOnly?: boolean
  onDraftChange?: (draft: { subject: string; content: LexicalText; emailIntegrationId?: string }) => void
}) {
  const [draftId, setDraftId] = useState<string>(initialDraftId || "")
  const [sending, setSending] = useState(false)
  const [signatureInitializedDraftId, setSignatureInitializedDraftId] = useState<string | null>(null)
  const [scheduling, setScheduling] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [revisionPopoverOpen, setRevisionPopoverOpen] = useState(false)
  const [revisionPrompt, setRevisionPrompt] = useState("")
  const bodyViewportRef = useRef<HTMLDivElement>(null)
  const initialBodyScrollResetRef = useRef(false)

  const {
    drafts,
    attachments,
    templateAttachments: storeTemplateAttachments,
    createDraft,
    setSubject,
    setContent,
    addRecipient,
    removeRecipient,
    toggleCC,
    toggleBCC,
    removeDraft,
    setFrom,
    addAttachment,
    removeAttachment,
    setTemplateAttachments: setStoreTemplateAttachments,
    removeTemplateAttachment: removeStoreTemplateAttachment,
    markSignatureAppended,
  } = useEmailComposerStore()

  const entryId = useDialogEntryId()
  const bookId = useBookId()
  const localCloseRegistry = useDialogCloseRegistry()
  const { closeDialog, pinDialog, setPreventEscape } = useGlobalDialogStore()
  const { data: syncboxDraft, isPending: isSyncboxDraftPending } = useSyncboxEmailDraft(syncboxDraftId || "")
  const draft = draftId ? drafts[draftId] : undefined
  const resolvedHouseholdId = householdId ?? draft?.household_id
  const { data: household } = useFetchHousehold(resolvedHouseholdId ?? "", { enabled: !!resolvedHouseholdId })
  const { pinnedDialogs, closeDialog: closeDialogPinned, unpinDialog } = usePinnedDialogStore()
  const pinned = pinnedDialogs.some((dialog) => dialog.id === draftId)
  const createDraftMutation = useCreateSyncboxEmailDraft()
  const updateDraftMutation = useUpdateSyncboxEmailDraft()
  const reviseEmailMutation = useReviseEmail()
  const isRevising = reviseEmailMutation.isPending

  const draftAttachments = draftId ? attachments[draftId] || [] : []
  const draftTemplateAttachments = draftId ? storeTemplateAttachments[draftId] || [] : []
  const fileInputRef = useRef<HTMLInputElement>(null)
  const editorRef = useRef<EditorRefHandle>(null)
  const onDraftChangeRef = useRef(onDraftChange)
  onDraftChangeRef.current = onDraftChange
  const isEditorFocused = useEditorFocused(editorRef)
  const isEditingScheduledEmail = syncboxDraft?.status === "scheduled"

  // Use the shared email compose logic hook
  const composeLogic = useEmailComposeLogic({
    content: draft?.content,
    setContent: (content) => draftId && setContent(draftId, content),
    editorRef,
  })

  const {
    selectedAddress,
    setSelectedAddress,
    allSendableAddresses,
    fromPopoverOpen,
    setFromPopoverOpen,
    handleAddressChange,
    selectedIntegration,
    currentUser,
    isSenderReady,
    isSenderError,
    refetchSender,
    defaultSendingIntegration,
    sendingIntegrations,
    templateAttachments: hookTemplateAttachments,
    getSignatureNodes,
    getSignatureHtml,
    contentContainsSignature,
    appendSignature,
  } = composeLogic
  const [defaultSenderUnavailable, setDefaultSenderUnavailable] = useState(false)
  const emailDisconnected =
    isSenderReady &&
    (sendingIntegrations.length === 0 ||
      (!!selectedAddress &&
        !sendingIntegrations.some((integration) => integration.id === selectedAddress.integrationId)))
  const isComposerReady = signatureInitializedDraftId === draftId || (isSenderReady && sendingIntegrations.length === 0)
  const selectedAddressRef = useRef(selectedAddress)
  selectedAddressRef.current = selectedAddress
  const selectableFromAddresses = useMemo(
    () =>
      integrationSelectionOnly
        ? sendingIntegrations.map((integration) => {
            const defaultAddress = integration.sending_addresses?.find((address) => address.is_default)

            return {
              email: defaultAddress?.email ?? integration.connected_email,
              displayName: defaultAddress?.display_name || undefined,
              integrationId: integration.id,
              isPrimaryIntegration: integration.is_primary,
            }
          })
        : allSendableAddresses,
    [allSendableAddresses, integrationSelectionOnly, sendingIntegrations]
  )

  // Sync template attachments from hook to store
  useEffect(() => {
    if (draftId && hookTemplateAttachments.length > 0) {
      setStoreTemplateAttachments(draftId, hookTemplateAttachments)
    }
  }, [draftId, hookTemplateAttachments, setStoreTemplateAttachments])

  const {
    trigger: triggerVoiceTranscription,
    isActive: isVoiceActive,
    stop: stopVoiceTranscription,
  } = useVoiceTranscriptionStore()

  const hasEmailContent = Boolean(
    draft?.subject?.trim() ||
      (draft?.content?.html && draft.content.html !== "<p></p>" && draft.content.html.trim() !== "")
  )

  const handleRevisionPopoverChange = (open: boolean) => {
    if (open) {
      if (!draftId || !drafts[draftId]?.content) {
        toast.error("Add content to your email before asking for revisions")
        return
      }
      reviseEmailMutation.reset()
      setRevisionPopoverOpen(true)
      return
    }

    if (isRevising) return
    setRevisionPopoverOpen(false)
    reviseEmailMutation.reset()
  }

  const runRevision = (instructions: string) => {
    if (!draftId) return

    const currentDraft = drafts[draftId]
    if (!currentDraft || !currentDraft.content) {
      toast.error("Add content to your email before asking for revisions")
      return
    }

    const trimmed = instructions.trim()
    if (!trimmed) {
      toast.error("Describe how you'd like the email to change")
      return
    }

    const contentWithHtml = ensureHtmlContent(currentDraft.content)
    const htmlBody = contentWithHtml?.html?.trim()

    if (!htmlBody) {
      toast.error("We couldn't read the email body to revise it")
      return
    }

    reviseEmailMutation.reset()

    const revisionIntegrationId = selectedAddress?.integrationId
    const revisionFromEmail = selectedAddress?.email

    // Get the integration signature to remove before revision and add back after
    const integrationSignatureHtml = getSignatureHtml()

    // Remove signature from current body before sending to backend
    const bodyWithoutSignature = integrationSignatureHtml
      ? removeTrailingSignature(htmlBody, integrationSignatureHtml).bodyHtml
      : htmlBody

    setRevisionPopoverOpen(false)

    reviseEmailMutation.mutate(
      {
        current_body: bodyWithoutSignature,
        revision_prompt: trimmed,
        subject: currentDraft.subject ?? "",
        household_id: currentDraft.household_id,
        email_integration_id: revisionIntegrationId,
        from_email: revisionFromEmail,
      },
      {
        onSuccess: (data) => {
          const currentAddress = selectedAddressRef.current
          if (currentAddress?.integrationId !== revisionIntegrationId || currentAddress?.email !== revisionFromEmail) {
            toast.info("Revision not applied", {
              description: "The From address changed while the email was being revised.",
            })
            return
          }

          const revisedBody = data.revised_body || ""

          // Append the integration signature if one exists
          const finalHtml = integrationSignatureHtml ? `${revisedBody}${integrationSignatureHtml}` : revisedBody

          // Convert to nodes and merge with signature nodes if available
          const bodyNodes = htmlToNodes(revisedBody) ?? STARTING_NODES
          const integrationSignatureNodes = getSignatureNodes()
          const mergedNodes = integrationSignatureNodes
            ? mergeLexicalContents([{ nodes: bodyNodes }, { nodes: integrationSignatureNodes }])
            : bodyNodes

          setContent(draftId, {
            html: finalHtml,
            nodes: mergedNodes,
            preferred_format: "html",
          } as LexicalText)

          if (typeof data.subject === "string") {
            setSubject(draftId, data.subject)
          }

          toast.success("Email updated")
          setRevisionPrompt("")
        },
        onError: () => {
          setRevisionPrompt(trimmed)
          setRevisionPopoverOpen(true)
          reviseEmailMutation.reset()
        },
      }
    )
  }

  const handleApplyRevision = () => runRevision(revisionPrompt)

  const handleApplyTemplate = (
    subject: string,
    body: LexicalText,
    templateAttachmentsList?: { signed_id: string; file_name: string }[]
  ) => {
    if (!draftId) return

    setSubject(draftId, subject)

    // Get the integration signature to append after template body
    const integrationSignatureNodes = getSignatureNodes()

    // Use nodes directly if available, otherwise convert from HTML
    const bodyNodes = body.nodes ?? htmlToNodes(body.html || "") ?? STARTING_NODES

    // Merge with signature nodes if available
    const mergedNodes = integrationSignatureNodes
      ? mergeLexicalContents([{ nodes: bodyNodes }, { nodes: integrationSignatureNodes }])
      : bodyNodes

    const finalContent = {
      nodes: mergedNodes,
      preferred_format: "html",
    } as LexicalText

    // Push through the store only. The editor re-hydrates from this via its
    // reactive effect (the same path AI revision and signature append use);
    // calling updateContent() here too would double-apply and race. See HELP-1190.
    setContent(draftId, finalContent)

    // Set template attachments to be included when sending
    if (!embedded && templateAttachmentsList?.length) {
      setStoreTemplateAttachments(
        draftId,
        templateAttachmentsList.map((a) => ({ signed_id: a.signed_id, file_name: a.file_name }))
      )
    } else if (!embedded) {
      setStoreTemplateAttachments(draftId, [])
    }
  }

  const { data: usersData } = useFetchUsersWithEmails()

  const suggestions = useMemo(() => {
    const userSuggestions: EmailAddressSuggestion[] =
      usersData?.users?.flatMap((user) =>
        user.all_emails.map((email) => ({
          email,
          name: user.full_name,
          type: "user" as const,
          personId: user.id,
        }))
      ) ?? []

    const peopleSuggestions: EmailAddressSuggestion[] = recommendedEmails ?? []

    const seen = new Set<string>()
    return [...peopleSuggestions, ...userSuggestions].filter((s) => {
      if (seen.has(s.email)) return false
      seen.add(s.email)
      return true
    })
  }, [usersData, recommendedEmails])

  const allRecipientEmails = useMemo(() => {
    const emails = new Set<string>()
    for (const r of draft?.to_recipients ?? []) emails.add(r.email)
    for (const r of draft?.cc_recipients ?? []) emails.add(r.email)
    for (const r of draft?.bcc_recipients ?? []) emails.add(r.email)
    return emails
  }, [draft?.to_recipients, draft?.cc_recipients, draft?.bcc_recipients])

  useLayoutEffect(() => {
    if (!embedded && entryId) setPreventEscape(true, entryId)
    if (!draftId && !initialDraftId && !syncboxDraftId) {
      const newDraftId = createDraft({ household_id: householdId } as EmailMessageDraft)
      setDraftId(newDraftId)
      setSubject(newDraftId, startingSubject || "")

      // Signature insertion operates on nodes, including for HTML-only intake invitations.
      setContent(newDraftId, {
        ...startingContent,
        html: startingContent?.html ?? startingHTML,
        nodes:
          startingContent?.nodes ??
          htmlToNodes(startingContent?.html ?? startingHTML ?? "<p></p><p></p>") ??
          STARTING_NODES,
        preferred_format: "html",
      })
    }
  }, [
    entryId,
    setPreventEscape,
    draftId,
    initialDraftId,
    syncboxDraftId,
    createDraft,
    setContent,
    startingContent,
    startingHTML,
    startingSubject,
    setSubject,
    householdId,
    embedded,
  ])

  useEffect(() => {
    if (!embedded || !draft?.content || !isComposerReady) return

    onDraftChangeRef.current?.({
      subject: draft.subject || "",
      content: ensureHtmlContent(draft.content) || draft.content,
      emailIntegrationId: selectedAddress?.integrationId,
    })
  }, [draft?.content, draft?.subject, embedded, selectedAddress?.integrationId, isComposerReady])

  useEffect(() => {
    if (!embedded || !draftId || !draft?.content || initialBodyScrollResetRef.current) return

    initialBodyScrollResetRef.current = true
    let secondAnimationFrame = 0
    const firstAnimationFrame = window.requestAnimationFrame(() => {
      if (bodyViewportRef.current) bodyViewportRef.current.scrollTop = 0
      secondAnimationFrame = window.requestAnimationFrame(() => {
        if (bodyViewportRef.current) bodyViewportRef.current.scrollTop = 0
      })
    })

    return () => {
      window.cancelAnimationFrame(firstAnimationFrame)
      window.cancelAnimationFrame(secondAnimationFrame)
    }
  }, [draft?.content, draftId, embedded])

  useEffect(
    () => () => {
      if (embedded && draftId) removeDraft(draftId)
    },
    [draftId, embedded, removeDraft]
  )

  useEffect(() => {
    if (syncboxDraftId && syncboxDraft && !draftId) {
      const draftContentNodes =
        syncboxDraft.content?.nodes ??
        htmlToNodes(syncboxDraft.body || syncboxDraft.content?.html || "", { wrapAsRaw: false })
      const baseNodes = draftContentNodes || STARTING_NODES

      const scheduledFor =
        typeof syncboxDraft.scheduled_for === "string"
          ? syncboxDraft.scheduled_for
          : syncboxDraft.scheduled_for?.toISOString()

      const newDraftId = createDraft({
        subject: syncboxDraft.subject || "",
        to_recipients: syncboxDraft.to_recipients,
        cc_recipients: syncboxDraft.cc_recipients,
        bcc_recipients: syncboxDraft.bcc_recipients,
        showCC: syncboxDraft.cc_recipients.length > 0,
        showBCC: syncboxDraft.bcc_recipients.length > 0,
        from: syncboxDraft.from || undefined,
        email_integration_id: syncboxDraft.email_integration_id,
        scheduled_for: syncboxDraft.status === "scheduled" ? scheduledFor : undefined,
        content: { nodes: baseNodes } as LexicalText,
        household_id: householdId,
        replyToSyncboxMessageId,
      } as EmailMessageDraft)
      setDraftId(newDraftId)

      const existingTemplateAttachments = (syncboxDraft.email_attachments ?? [])
        .filter((a) => a.signed_id)
        .map((a) => ({ signed_id: a.signed_id as string, file_name: a.filename, url: a.url }))
      if (existingTemplateAttachments.length > 0) {
        setStoreTemplateAttachments(newDraftId, existingTemplateAttachments)
      }
    }
  }, [
    syncboxDraft,
    syncboxDraftId,
    draftId,
    createDraft,
    householdId,
    setStoreTemplateAttachments,
    replyToSyncboxMessageId,
  ])

  // Resolve once, after both the draft and current-user access have loaded.
  useEffect(() => {
    if (selectedAddress || !draft || !isSenderReady) return
    if (syncboxDraftId && isSyncboxDraftPending) return

    const resolved = resolveCompositionSender({
      draft,
      integrations: sendingIntegrations,
      user: integrationSelectionOnly || replyToSyncboxMessageId ? undefined : currentUser,
      ready: isSenderReady,
    })
    const address = integrationSelectionOnly && !draft.from?.email ? selectableFromAddresses[0] : resolved.address
    if (address) {
      setSelectedAddress(address)
      setDefaultSenderUnavailable(resolved.unavailable)
    }
  }, [
    selectedAddress,
    draft,
    isSenderReady,
    syncboxDraftId,
    isSyncboxDraftPending,
    sendingIntegrations,
    integrationSelectionOnly,
    replyToSyncboxMessageId,
    currentUser,
    selectableFromAddresses,
    setSelectedAddress,
  ])

  // Sync from address to draft and handle signature appending
  useEffect(() => {
    if (!draftId || !draft || !selectedAddress) return

    if (draft.from?.email !== selectedAddress.email || draft.email_integration_id !== selectedAddress.integrationId) {
      setFrom(draftId, { email: selectedAddress.email }, selectedAddress.integrationId)
    }

    if (!isSenderReady || signatureInitializedDraftId === draftId) return

    const currentNodes = draft.content?.nodes ?? htmlToNodes(draft.content?.html || "") ?? STARTING_NODES
    if (!contentContainsSignature(currentNodes)) {
      const signedNodes = appendSignature(currentNodes)
      if (signedNodes !== currentNodes) {
        setContent(draftId, { nodes: signedNodes, preferred_format: "html" })
      }
    }

    markSignatureAppended(draftId)
    setSignatureInitializedDraftId(draftId)
  }, [
    draftId,
    draft,
    selectedAddress,
    contentContainsSignature,
    appendSignature,
    isSenderReady,
    setContent,
    setFrom,
    markSignatureAppended,
    signatureInitializedDraftId,
  ])

  useEffect(() => {
    if (!draftId || !!syncboxDraftId || !!initialDraftId) return

    if (toEmails?.length) {
      toEmails.forEach((email) => {
        addRecipient(draftId, "to_recipients", { email: email.email })
      })
    }

    if (ccEmails?.length) {
      toggleCC(draftId)
      ccEmails.forEach((email) => {
        addRecipient(draftId, "cc_recipients", { email: email.email })
      })
    }

    if (bccEmails?.length) {
      toggleBCC(draftId)
      bccEmails.forEach((email) => {
        addRecipient(draftId, "bcc_recipients", { email: email.email })
      })
    }
  }, [draftId, toEmails, ccEmails, bccEmails, addRecipient, toggleCC, toggleBCC, syncboxDraftId, initialDraftId])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!draftId) return
    const files = event.target.files
    if (!files) return

    const currentTotalSize = draftAttachments.reduce((sum, a) => sum + a.file.size, 0)

    Array.from(files).forEach((file) => {
      if (draftAttachments.length >= MAX_ATTACHMENTS) {
        toast.error(`Maximum ${MAX_ATTACHMENTS} attachments allowed`)
        return
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}`)
        return
      }

      const newTotalSize = currentTotalSize + file.size
      if (newTotalSize > MAX_TOTAL_ATTACHMENT_SIZE) {
        toast.error(`Total attachment size cannot exceed ${formatFileSize(MAX_TOTAL_ATTACHMENT_SIZE)}`)
        return
      }

      addAttachment(draftId, file)
    })

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleAttachmentClick = () => {
    fileInputRef.current?.click()
  }

  const handleRemoveAttachment = (attachmentId: string) => {
    if (!draftId) return
    removeAttachment(draftId, attachmentId)
  }

  const handleClose = useCallback(() => {
    if (!draftId) return
    removeDraft(draftId)

    if (onClose) {
      onClose()
    } else {
      if (pinnedDialogs.find((d) => d.id === draftId)) {
        closeDialogPinned(draftId)
      } else {
        closeDialog()
      }
    }
  }, [draftId, removeDraft, onClose, pinnedDialogs, closeDialogPinned, closeDialog])

  const contentForDirtyCheck = draft?.content?.html ? `${draft.subject || ""}\n${draft.content.html}` : undefined
  const [showConfirm, setShowConfirm] = useState(false)
  const initialHtmlRef = useRef<string | null>(null)

  useEffect(() => {
    if (initialHtmlRef.current === null && contentForDirtyCheck) {
      initialHtmlRef.current = contentForDirtyCheck
    }
  }, [contentForDirtyCheck])

  const isDirty = initialHtmlRef.current !== null && contentForDirtyCheck !== initialHtmlRef.current

  const requestClose = useCallback(() => {
    if (isDirty) {
      setShowConfirm(true)
    } else {
      handleClose()
    }
  }, [isDirty, handleClose])

  useEffect(() => {
    if (embedded) return

    if (!entryId) return localCloseRegistry?.register(requestClose)
    setCloseAttemptHandler(requestClose, entryId)
    return () => setCloseAttemptHandler(null, entryId)
  }, [embedded, requestClose, entryId, localCloseRegistry])

  const handlePin = () => {
    if (!draftId) return
    if (pinnedDialogs.find((d) => d.id === draftId)) {
      unpinDialog(draftId)
    } else {
      pinDialog(draftId, "email", bookId)
    }
  }

  const validateDraft = (draftToValidate: EmailMessageDraft): boolean => {
    if (!isSenderReady || emailDisconnected || !selectedAddress) return false
    if (!draftToValidate.subject?.trim()) {
      toast.error("Please add a subject")
      return false
    }
    if (!draftToValidate.to_recipients?.length) {
      toast.error("Please add at least one recipient")
      return false
    }
    if (!hasVisibleOutgoingContent(draftToValidate.content)) {
      toast.error("Please add content to your email")
      return false
    }
    return true
  }

  const processAttachments = async () => {
    return Promise.all(
      draftAttachments.map(
        (attachment) =>
          new Promise<{ filename: string; content_type: string; size: number; file: string }>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
              const base64Data = (reader.result as string).split(",")[1]
              resolve({
                filename: attachment.file.name,
                content_type: attachment.file.type,
                size: attachment.file.size,
                file: base64Data,
              })
            }
            reader.onerror = reject
            reader.readAsDataURL(attachment.file)
          })
      )
    )
  }

  type AttachmentAttributes = { filename: string; content_type: string; size: number; file: string }[]

  const buildEmailParams = (
    draftToBuild: EmailMessageDraft,
    attachmentAttributes: AttachmentAttributes,
    scheduledFor?: string
  ): SyncboxEmailDraftParams => ({
    email_integration_id: selectedAddress?.integrationId,
    subject: draftToBuild.subject,
    content: ensureHtmlContent(draftToBuild.content),
    from_attributes: draftToBuild.from,
    to_recipients_attributes: draftToBuild.to_recipients,
    cc_recipients_attributes: draftToBuild.showCC ? draftToBuild.cc_recipients : [],
    bcc_recipients_attributes: draftToBuild.showBCC ? draftToBuild.bcc_recipients : [],
    email_attachments_attributes: attachmentAttributes.length > 0 ? attachmentAttributes : undefined,
    reply_to_syncbox_message_id: draftToBuild.replyToSyncboxMessageId,
    ...(scheduledFor && { scheduled_for: scheduledFor }),
  })

  const draftWithCurrentEditorContent = (): EmailMessageDraft | null => {
    if (!draftId || !drafts[draftId] || !isComposerReady) return null

    const result = readOutgoingContent(editorRef.current)
    if (!result.ok) {
      setSendError(EMAIL_EDITOR_SYNC_ERROR)
      return null
    }

    return { ...drafts[draftId], content: result.content }
  }

  const sendEmail = async () => {
    if (isRevising || !draftId || !drafts[draftId]) return

    const currentDraft = draftWithCurrentEditorContent()
    if (!currentDraft) return
    if (!validateDraft(currentDraft)) return

    setSending(true)
    setSendError(null)

    try {
      const attachmentAttributes = await processAttachments()

      const templateSignedIds = draftTemplateAttachments.map((a) => a.signed_id)
      createDraftMutation.mutate(
        {
          emailDraft: buildEmailParams(currentDraft, attachmentAttributes),
          templateAttachmentSignedIds: templateSignedIds.length > 0 ? templateSignedIds : undefined,
        },
        {
          onSuccess: () => {
            toast.success("Email queued for sending")
            setSending(false)
            if (onSent) onSent()
            else handleClose()
          },
          onError: (error) => {
            setSendError(error instanceof Error ? error.message : "Failed to send email")
            setSending(false)
            void refetchSender()
          },
        }
      )
    } catch (error) {
      console.error("Failed to process attachments:", error)
      toast.error("Failed to process attachments")
      setSending(false)
    }
  }

  const scheduleEmail = async (scheduledFor: string) => {
    if (isRevising || !draftId || !drafts[draftId]) return

    const currentDraft = draftWithCurrentEditorContent()
    if (!currentDraft) return
    if (!validateDraft(currentDraft)) return

    setScheduling(true)
    setSendError(null)

    try {
      const attachmentAttributes = await processAttachments()

      const templateSignedIds = draftTemplateAttachments.map((a) => a.signed_id)

      if (isEditingScheduledEmail && syncboxDraftId) {
        updateDraftMutation.mutate(
          {
            id: syncboxDraftId,
            emailDraft: buildEmailParams(currentDraft, attachmentAttributes, scheduledFor),
            templateAttachmentSignedIds: templateSignedIds.length > 0 ? templateSignedIds : undefined,
          },
          {
            onSuccess: () => {
              toast.success("Scheduled email updated")
              setScheduling(false)
              if (onSent) onSent()
              else handleClose()
            },
            onError: () => {
              setScheduling(false)
            },
          }
        )
        return
      }

      createDraftMutation.mutate(
        {
          emailDraft: buildEmailParams(currentDraft, attachmentAttributes, scheduledFor),
          templateAttachmentSignedIds: templateSignedIds.length > 0 ? templateSignedIds : undefined,
        },
        {
          onSuccess: () => {
            toast.success("Email successfully scheduled")
            setScheduling(false)
            if (onSent) onSent()
            else handleClose()
          },
          onError: () => {
            setScheduling(false)
          },
        }
      )
    } catch (error) {
      console.error("Failed to process attachments:", error)
      toast.error("Failed to process attachments")
      setScheduling(false)
    }
  }

  const saveScheduledEmail = async () => {
    if (isRevising || !draftId || !drafts[draftId] || !syncboxDraftId) return

    const currentDraft = draftWithCurrentEditorContent()
    if (!currentDraft) return
    if (!validateDraft(currentDraft)) return

    const seededScheduledFor =
      typeof syncboxDraft?.scheduled_for === "string"
        ? syncboxDraft.scheduled_for
        : syncboxDraft?.scheduled_for?.toISOString()
    const scheduledFor = currentDraft.scheduled_for || seededScheduledFor
    if (!scheduledFor) {
      toast.error("Please choose when to send this email")
      return
    }

    setSending(true)
    setSendError(null)

    try {
      const attachmentAttributes = await processAttachments()

      const templateSignedIds = draftTemplateAttachments.map((a) => a.signed_id)
      updateDraftMutation.mutate(
        {
          id: syncboxDraftId,
          emailDraft: buildEmailParams(currentDraft, attachmentAttributes, scheduledFor),
          templateAttachmentSignedIds: templateSignedIds.length > 0 ? templateSignedIds : undefined,
        },
        {
          onSuccess: () => {
            toast.success("Scheduled email updated")
            setSending(false)
            if (onSent) onSent()
            else handleClose()
          },
          onError: () => {
            setSending(false)
          },
        }
      )
    } catch (error) {
      console.error("Failed to process attachments:", error)
      toast.error("Failed to process attachments")
      setSending(false)
    }
  }

  if (!draftId || !draft) {
    return (
      <CustomDialogContent title="Email">
        <div className="flex h-full w-full flex-col items-center justify-center">
          <p className="text-tertiary-foreground text-sm">Loading...</p>
        </div>
      </CustomDialogContent>
    )
  }

  return (
    <CustomDialogContent title="Email">
      <div
        className={cn(
          "relative flex h-full min-h-0 w-full flex-col overflow-hidden transition-colors duration-200",
          embedded ? "max-h-none" : "max-h-[70vh]",
          revisionPopoverOpen && "bg-tertiary"
        )}
      >
        <div className="flex flex-none items-center justify-between border-b px-4 py-2">
          <FromEmailSelector
            selectedAddress={selectedAddress}
            allAddresses={selectableFromAddresses}
            isOpen={fromPopoverOpen}
            onOpenChange={setFromPopoverOpen}
            onSelect={handleAddressChange}
            emptyLabel={isSenderReady ? "No connected account" : "Loading..."}
          />
          {defaultSenderUnavailable && (
            <p className="text-muted-foreground text-xs" role="status">
              Default sender unavailable. Using another account.
            </p>
          )}
          {!embedded && (
            <div className="flex items-center gap-0.5">
              {!hidePinning && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="iconSm"
                      variant={pinned ? "secondary" : "ghost"}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        handlePin()
                      }}
                    >
                      <PictureInPicture2 className="text-icon" />
                      <span className="sr-only">{pinned ? "Unpin" : "Pin to screen"}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{pinned ? "Unpin" : "Pin to screen"}</TooltipContent>
                </Tooltip>
              )}
              <Button size="iconSm" variant="ghost" onClick={requestClose}>
                <X className="text-icon" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex-none border-b px-4 py-2">
          <EmailReferenceInput
            recipients={draft.to_recipients || []}
            label="To"
            showCC={draft.showCC}
            showBCC={draft.showBCC}
            toggleCC={() => toggleCC(draftId)}
            toggleBCC={() => toggleBCC(draftId)}
            suggestions={suggestions}
            excludeEmails={allRecipientEmails}
            maxLength={5}
            onAddRecipient={(recipient) => addRecipient(draftId, "to_recipients", recipient)}
            onRemoveRecipient={(email) => removeRecipient(draftId, "to_recipients", email)}
            onSwitchRecipient={(oldEmail, newRecipient) => {
              removeRecipient(draftId, "to_recipients", oldEmail)
              addRecipient(draftId, "to_recipients", newRecipient)
            }}
            readOnly={embedded}
          />
        </div>
        {draft.showCC && (
          <div className="flex-none border-b px-4 py-2">
            <EmailReferenceInput
              recipients={draft.cc_recipients || []}
              label="Cc"
              suggestions={suggestions}
              excludeEmails={allRecipientEmails}
              maxLength={5}
              onAddRecipient={(recipient) => addRecipient(draftId, "cc_recipients", recipient)}
              onRemoveRecipient={(email) => removeRecipient(draftId, "cc_recipients", email)}
              onSwitchRecipient={(oldEmail, newRecipient) => {
                removeRecipient(draftId, "cc_recipients", oldEmail)
                addRecipient(draftId, "cc_recipients", newRecipient)
              }}
            />
          </div>
        )}
        {draft.showBCC && (
          <div className="flex-none border-b px-4 py-2">
            <EmailReferenceInput
              recipients={draft.bcc_recipients || []}
              label="Bcc"
              suggestions={suggestions}
              excludeEmails={allRecipientEmails}
              maxLength={5}
              onAddRecipient={(recipient) => addRecipient(draftId, "bcc_recipients", recipient)}
              onRemoveRecipient={(email) => removeRecipient(draftId, "bcc_recipients", email)}
              onSwitchRecipient={(oldEmail, newRecipient) => {
                removeRecipient(draftId, "bcc_recipients", oldEmail)
                addRecipient(draftId, "bcc_recipients", newRecipient)
              }}
            />
          </div>
        )}
        <EmailSubjectInput value={draft.subject || ""} onChange={(subject) => setSubject(draftId, subject)} />
        {sendError && (
          <div className="bg-red-background text-red-accent border-red-border flex flex-none items-start gap-2 border-b px-4 py-2 text-sm">
            <AlertTriangle className="mt-0.5 size-4 flex-none" />
            <p>{sendError}</p>
          </div>
        )}
        <div className="relative min-h-0 grow overflow-hidden">
          {isRevising && (
            <div className="bg-background border-border/50 absolute inset-0 z-10 flex items-center justify-center border shadow-sm">
              <Loader2 className="text-muted-foreground size-5 animate-spin" />
            </div>
          )}
          <ScrollArea className="h-full" viewportRef={bodyViewportRef}>
            {isComposerReady ? (
              <EmailBodyEditor
                ref={editorRef}
                onChange={(content) => setContent(draftId, content)}
                initialContent={draft.content}
                provider={selectedIntegration?.provider ?? defaultSendingIntegration?.provider}
                reserveFloatingFormatBarSpace
              />
            ) : isSenderError ? (
              <div className="space-y-2 px-4 py-4" role="alert">
                <p className="text-muted-foreground text-sm">Couldn’t load your sender settings.</p>
                <Button variant="outline" size="sm" onClick={() => void refetchSender()}>
                  Retry
                </Button>
              </div>
            ) : (
              <p className="text-muted-foreground px-4 py-4 text-sm">Loading email...</p>
            )}
          </ScrollArea>
        </div>
        {(draftAttachments.length > 0 || draftTemplateAttachments.length > 0) && (
          <div className="border-t px-4 py-2">
            <div className="flex flex-wrap gap-2">
              {draftAttachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="bg-secondary text-secondary-foreground flex items-center gap-2 rounded-md px-3 py-1.5 text-sm"
                >
                  <Paperclip className="size-3.5" />
                  <span className="max-w-[200px] truncate">{attachment.file.name}</span>
                  <span className="text-muted-foreground text-xs">({formatFileSize(attachment.file.size)})</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(attachment.id)}
                    className="hover:text-destructive ml-1"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
              {draftTemplateAttachments.map((attachment) => (
                <div
                  key={attachment.signed_id}
                  className="bg-secondary text-secondary-foreground flex items-center gap-2 rounded-md px-3 py-1.5 text-sm"
                >
                  <Paperclip className="size-3.5" />
                  {attachment.url ? (
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noreferrer"
                      className="max-w-[200px] truncate underline-offset-2 hover:underline"
                    >
                      {attachment.file_name}
                    </a>
                  ) : (
                    <span className="max-w-[200px] truncate">{attachment.file_name}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => draftId && removeStoreTemplateAttachment(draftId, attachment.signed_id)}
                    className="hover:text-destructive ml-1"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="relative">
          <FloatingFormatBar editorRef={editorRef} visible={isEditorFocused} />
          <TooltipProvider>
            <div className="flex justify-between border-t px-4 py-2">
              <div className="flex items-center gap-1">
                <Popover open={revisionPopoverOpen} onOpenChange={handleRevisionPopoverChange}>
                  <PopoverTrigger asChild>
                    <Button size="iconSm" variant="ghost" disabled={isRevising} tooltip="Rewrite with AI">
                      {isRevising ? <Loader2 className="text-icon animate-spin" /> : <Sparkles className="text-icon" />}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" side="top" sideOffset={8} className="w-96 space-y-2 px-3 py-2">
                    <div className="space-y-2">
                      <Label htmlFor="revision-prompt">Describe the changes you want</Label>
                      <Textarea
                        id="revision-prompt"
                        value={revisionPrompt}
                        onChange={(event) => setRevisionPrompt(event.target.value)}
                        placeholder="e.g. Make this warmer and mention follow up on our last meeting."
                        rows={4}
                        className="px-2 py-1"
                        disabled={isRevising}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          handleRevisionPopoverChange(false)
                          setRevisionPrompt("")
                        }}
                        disabled={isRevising}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleApplyRevision}
                        disabled={isRevising || !revisionPrompt.trim()}
                      >
                        {isRevising ? (
                          <>
                            <Loader2 className="size-4 animate-spin" />
                            Applying...
                          </>
                        ) : (
                          "Rewrite email"
                        )}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
                <EmailTemplatePicker
                  householdId={householdId}
                  hasContent={hasEmailContent}
                  onApply={handleApplyTemplate}
                  allowAttachments={!embedded}
                />
                {!embedded && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileSelect}
                      accept="*/*"
                    />
                    <Button
                      size="iconSm"
                      variant="ghost"
                      onClick={handleAttachmentClick}
                      disabled={draftAttachments.length >= MAX_ATTACHMENTS}
                      tooltip="Attach file"
                    >
                      <Paperclip className="text-icon" />
                    </Button>
                  </>
                )}
                <Button
                  size="iconSm"
                  variant={isVoiceActive ? "secondary" : "ghost"}
                  onClick={() => {
                    if (isVoiceActive) {
                      stopVoiceTranscription()
                    } else {
                      editorRef.current?.focus()
                      setTimeout(() => triggerVoiceTranscription(), 50)
                    }
                  }}
                  tooltip={isVoiceActive ? "Stop dictation" : "Dictate"}
                >
                  <Mic className={isVoiceActive ? "text-red-accent animate-pulse" : "text-icon"} />
                </Button>
              </div>
              {!embedded && (
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex" tabIndex={emailDisconnected ? 0 : undefined}>
                        <ScheduledSendButton
                          onSend={isEditingScheduledEmail ? saveScheduledEmail : sendEmail}
                          onSchedule={scheduleEmail}
                          sendLabel={isEditingScheduledEmail ? "Save" : "Send"}
                          sendingLabel={isEditingScheduledEmail ? "Saving..." : "Sending..."}
                          sendIcon={isEditingScheduledEmail ? <Save /> : undefined}
                          disabled={
                            isRevising || !isComposerReady || !isSenderReady || emailDisconnected || !selectedAddress
                          }
                          sending={sending}
                          scheduling={scheduling}
                          recipientTimezone={household?.timezone}
                        />
                      </span>
                    </TooltipTrigger>
                    {emailDisconnected && <TooltipContent>Email is not connected</TooltipContent>}
                  </Tooltip>
                </div>
              )}
            </div>
          </TooltipProvider>
        </div>
        {!embedded && (
          <ConfirmDialog
            open={showConfirm}
            onOpenChange={setShowConfirm}
            title={"Discard unsent email?"}
            description={"Any unsaved changes will be lost. Are you sure you would like to close this draft?"}
            dismiss={"explicit"}
            cancel={{ onClick: () => setShowConfirm(false) }}
            action={{ label: "Discard", onClick: handleClose }}
          />
        )}
      </div>
    </CustomDialogContent>
  )
}
